import { createSlice } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import R from "ramda";

import MakePresenter from "presenters/MakePresenter";
import MakeRepository from "repositories/MakeRepository";
import { deserialize, deserializeErrors } from "utils/storeUtils";

const initialState = {
  make: null,
  makes: null,
  meta: null,
};

const makesSlice = createSlice({
  name: "makes",
  initialState,
  reducers: {
    setMake(state, { payload }) {
      state.make = payload;
    },
    clearMakeSuccess(state) {
      state.make = null;
    },
    loadMakesSuccess(state, { payload }) {
      state.makes = payload.makes;
      state.meta = payload.meta;
    },
    loadMoreMakesSuccess(state, { payload }) {
      state.makes = R.concat(state.makes, payload);
    },
    clearMakesSuccess(state) {
      state.makes = null;
    },
    deleteMakeSuccess(state, { payload }) {
      const newMakes = state.makes.filter(({ id }) => id !== payload);
      const newMeta = { ...state.meta, totalCount: state.meta.totalCount - 1 };
      state.makes = newMakes;
      state.meta = newMeta;
    },
    patchMakeSuccess(state, { payload }) {
      const params = R.isNil(payload.params) ? null : { ...state.make[`${payload.key}`], ...payload.params };
      state.make[`${payload.key}`] = params;
    },

    updateMakeSuccess(state, { payload }) {
      state.makes = payload.makes;
      state.meta = payload.meta;
    },
  },
});

export const { actions } = makesSlice;
export default makesSlice.reducer;

export const useMakesActions = () => {
  const dispatch = useDispatch();

  const createMake = (params) => {
    return MakeRepository.create(params)
      .then(({ data }) => {
        const make = deserialize(data);
        dispatch(makesSlice.actions.setMake(make));
        return make;
      })
      .catch((error) => {
        return Promise.reject(deserializeErrors(error));
      });
  };

  const loadMake = (slug) => {
    return MakeRepository.show(slug).then(({ data }) => {
      const make = deserialize(data);
      dispatch(makesSlice.actions.setMake(make));
      return make;
    });
  };

  const loadMakes = (params) => {
    return MakeRepository.index(params).then(({ data }) => {
      const makes = deserialize(data);
      dispatch(makesSlice.actions.loadMakesSuccess({ makes, meta: data.meta }));
      return makes;
    });
  };

  const loadMoreMakes = (params) => {
    return MakeRepository.index(params).then(({ data }) => {
      const makes = deserialize(data);
      dispatch(makesSlice.actions.loadMoreMakesSuccess(makes));
      return makes;
    });
  };

  const deleteMake = (slug) => {
    return MakeRepository.destroy(slug).then(() => {
      dispatch(makesSlice.actions.deleteMakeSuccess(slug));
    });
  };

  const clearMake = () => dispatch(makesSlice.actions.clearMakeSuccess());
  const clearMakes = () => dispatch(makesSlice.actions.clearMakesSuccess());

  const patchMake = (key, params) => dispatch(makesSlice.actions.patchMakeSuccess({ key, params }));

  const updateMake = (make) => {
    const params = {
      data: {
        type: "makes",
        id: MakePresenter.id(make),
        attributes: {
          status: "live",
        },
      },
    };

    return MakeRepository.update(MakePresenter.id(make), params).catch((error) => {
      throw new Error(error);
    });
  };

  return {
    createMake,
    loadMake,
    clearMake,
    patchMake,
    loadMakes,
    clearMakes,
    loadMoreMakes,
    deleteMake,
    updateMake,
  };
};
